import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { 
  CheckCircle2, 
  XCircle,
  AlertTriangle,
  ArrowLeft,
  Camera,
  Flag,
  Home,
  Eye,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  Clock,
  BarChart3,
  Shield,
  FileWarning
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = getApiBaseUrl();

const ScanResults = () => {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const scanType = searchParams.get('type') || 'inspection';
  const navigate = useNavigate();
  const { token, user } = useAuth();
  
  // State
  const [session, setSession] = useState(null);
  const [flags, setFlags] = useState([]);
  const [artefacts, setArtefacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFlag, setSelectedFlag] = useState(null);
  const [outcomeDialog, setOutcomeDialog] = useState(false);
  const [outcomeNotes, setOutcomeNotes] = useState('');
  const [submittingOutcome, setSubmittingOutcome] = useState(false);
  
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'landlord';
  
  useEffect(() => {
    loadResults();
  }, [sessionId]);
  
  const loadResults = async () => {
    try {
      setLoading(true);
      
      // Load session details
      const sessionRes = await fetch(`${API_URL}/api/scanning/sessions/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (!sessionRes.ok) {
        throw new Error('Failed to load session');
      }
      
      const sessionData = await sessionRes.json();
      setSession(sessionData);
      
      // Load artefacts
      const artefactsRes = await fetch(`${API_URL}/api/scanning/sessions/${sessionId}/artefacts`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (artefactsRes.ok) {
        const artefactsData = await artefactsRes.json();
        setArtefacts(artefactsData);
      }
      
      // Load flags for inspection sessions
      if (sessionData.session_type === 'inspection') {
        const flagsRes = await fetch(`${API_URL}/api/scanning/flags?property_id=${sessionData.property_id}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        
        if (flagsRes.ok) {
          const flagsData = await flagsRes.json();
          setFlags(flagsData);
        }
      }
      
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleOutcome = async (flagId, outcome) => {
    try {
      setSubmittingOutcome(true);
      
      const response = await fetch(`${API_URL}/api/scanning/flags/${flagId}/outcome`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          outcome,
          outcome_notes: outcomeNotes,
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to record outcome');
      }
      
      toast.success(`Flag marked as ${outcome}`);
      setOutcomeDialog(false);
      setOutcomeNotes('');
      setSelectedFlag(null);
      
      // Reload flags
      loadResults();
      
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmittingOutcome(false);
    }
  };
  
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-500 text-white';
      case 'high': return 'bg-orange-500 text-white';
      case 'medium': return 'bg-amber-500 text-white';
      case 'low': return 'bg-blue-500 text-white';
      default: return 'bg-slate-500 text-white';
    }
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'pass': return 'bg-green-500';
      case 'pass_with_warnings': return 'bg-amber-500';
      case 'completed': return 'bg-blue-500';
      case 'failed': return 'bg-red-500';
      case 'processing': return 'bg-purple-500';
      default: return 'bg-slate-500';
    }
  };
  
  const getOutcomeIcon = (outcome) => {
    switch (outcome) {
      case 'confirmed': return <ThumbsUp className="h-4 w-4 text-green-500" />;
      case 'false_positive': return <ThumbsDown className="h-4 w-4 text-red-500" />;
      case 'follow_up': return <Clock className="h-4 w-4 text-amber-500" />;
      case 'insufficient_evidence': return <FileWarning className="h-4 w-4 text-slate-500" />;
      default: return <AlertTriangle className="h-4 w-4 text-amber-500" />;
    }
  };
  
  // Group artefacts by room
  const artefactsByRoom = artefacts.reduce((acc, art) => {
    const room = art.room_name || 'Property Level';
    if (!acc[room]) acc[room] = [];
    acc[room].push(art);
    return acc;
  }, {});
  
  // Stats
  const pendingFlags = flags.filter(f => f.outcome === 'pending').length;
  const confirmedFlags = flags.filter(f => f.outcome === 'confirmed').length;
  const falsePositives = flags.filter(f => f.outcome === 'false_positive').length;
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="scan-results-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading scan results...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 p-6" data-testid="scan-results-error">
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button onClick={() => navigate(-1)} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50" data-testid="scan-results-page">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-semibold">
                  {session?.session_type === 'baseline' ? 'Initial 3D' : 'Inspection'} Results
                </h1>
                <p className="text-sm text-slate-500">
                  Reference: {sessionId?.slice(0, 8)}...
                </p>
              </div>
            </div>
            <Badge className={getStatusColor(session?.status)}>
              {session?.status?.replace(/_/g, ' ').toUpperCase()}
            </Badge>
          </div>
        </div>
      </div>
      
      <div className="max-w-6xl mx-auto p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <BarChart3 className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Quality Score</p>
                  <p className="text-2xl font-bold">{session?.quality_score?.toFixed(0) || 'N/A'}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Shield className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Coverage</p>
                  <p className="text-2xl font-bold">{session?.coverage_score?.toFixed(0) || 'N/A'}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Flag className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Items Noted</p>
                  <p className="text-2xl font-bold">{flags.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Camera className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Photos</p>
                  <p className="text-2xl font-bold">{artefacts.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Main Content Tabs */}
        <Tabs defaultValue={flags.length > 0 ? 'flags' : 'captures'} className="space-y-4">
          <TabsList>
            <TabsTrigger value="flags" className="relative">
              Items Noted
              {pendingFlags > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-amber-500 text-white rounded-full">
                  {pendingFlags}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="captures">Photos by Room</TabsTrigger>
            <TabsTrigger value="details">Details</TabsTrigger>
          </TabsList>
          
          {/* Flags Tab */}
          <TabsContent value="flags">
            {flags.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Issues Found</h3>
                  <p className="text-slate-500">
                    {session?.session_type === 'baseline' 
                      ? 'Initial 3D documentation completed successfully.'
                      : 'The inspection found no concerns.'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Flag Summary */}
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex gap-6">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-amber-500" />
                        <span className="text-sm">Pending: {pendingFlags}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <ThumbsUp className="h-4 w-4 text-green-500" />
                        <span className="text-sm">Confirmed: {confirmedFlags}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <ThumbsDown className="h-4 w-4 text-red-500" />
                        <span className="text-sm">False Positives: {falsePositives}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Flag List */}
                {flags.map((flag) => (
                  <Card key={flag.id} className={flag.outcome === 'pending' ? 'border-amber-200' : ''}>
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className={`p-2 rounded-lg ${
                            flag.severity === 'critical' ? 'bg-red-100' :
                            flag.severity === 'high' ? 'bg-orange-100' :
                            flag.severity === 'medium' ? 'bg-amber-100' : 'bg-blue-100'
                          }`}>
                            <Flag className={`h-5 w-5 ${
                              flag.severity === 'critical' ? 'text-red-600' :
                              flag.severity === 'high' ? 'text-orange-600' :
                              flag.severity === 'medium' ? 'text-amber-600' : 'text-blue-600'
                            }`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium">{flag.reason_code.replace(/_/g, ' ')}</h4>
                              <Badge className={getSeverityColor(flag.severity)}>
                                {flag.severity}
                              </Badge>
                              {flag.outcome !== 'pending' && (
                                <div className="flex items-center gap-1 text-sm text-slate-500">
                                  {getOutcomeIcon(flag.outcome)}
                                  <span>{flag.outcome.replace(/_/g, ' ')}</span>
                                </div>
                              )}
                            </div>
                            <p className="text-sm text-slate-600 mb-2">{flag.description}</p>
                            {flag.room_name && (
                              <div className="flex items-center gap-2 text-sm text-slate-500">
                                <Home className="h-4 w-4" />
                                <span>{flag.room_name}</span>
                              </div>
                            )}
                            {/* AI Confidence - Admin only */}
                            {isAdmin && flag.confidence && (
                              <div className="mt-2">
                                <div className="flex items-center gap-2 text-xs text-slate-500">
                                  <span>Confidence:</span>
                                  <Progress value={flag.confidence * 100} className="w-24 h-2" />
                                  <span>{(flag.confidence * 100).toFixed(0)}%</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Actions */}
                        {isAdmin && flag.outcome === 'pending' && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-600 border-green-600 hover:bg-green-50"
                              onClick={() => {
                                setSelectedFlag(flag);
                                handleOutcome(flag.id, 'confirmed');
                              }}
                            >
                              <ThumbsUp className="h-4 w-4 mr-1" />
                              Confirm
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-600 hover:bg-red-50"
                              onClick={() => {
                                setSelectedFlag(flag);
                                handleOutcome(flag.id, 'false_positive');
                              }}
                            >
                              <ThumbsDown className="h-4 w-4 mr-1" />
                              False Positive
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedFlag(flag);
                                setOutcomeDialog(true);
                              }}
                            >
                              <MessageSquare className="h-4 w-4 mr-1" />
                              Review
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
          
          {/* Captures Tab */}
          <TabsContent value="captures">
            <div className="space-y-6">
              {Object.entries(artefactsByRoom).map(([room, roomArtefacts]) => (
                <Card key={room}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Home className="h-5 w-5" />
                      {room}
                      <Badge variant="secondary">{roomArtefacts.length} captures</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                      {roomArtefacts.map((art) => (
                        <div 
                          key={art.id} 
                          className="aspect-square rounded-lg bg-slate-100 border flex flex-col items-center justify-center p-2"
                        >
                          <Camera className="h-8 w-8 text-slate-400 mb-2" />
                          <span className="text-xs text-slate-500 text-center">
                            {art.artefact_kind?.replace(/_/g, ' ')}
                          </span>
                          {art.quality_metrics?.blur_score !== undefined && (
                            <span className="text-xs text-slate-400 mt-1">
                              Quality: {((1 - art.quality_metrics.blur_score) * 100).toFixed(0)}%
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
          
          {/* Details Tab */}
          <TabsContent value="details">
            <Card>
              <CardHeader>
                <CardTitle>Session Details</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm text-slate-500">Session ID</dt>
                    <dd className="font-mono text-sm">{session?.id}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-slate-500">Type</dt>
                    <dd className="capitalize">{session?.session_type}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-slate-500">Property ID</dt>
                    <dd className="font-mono text-sm">{session?.property_id}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-slate-500">Status</dt>
                    <dd>
                      <Badge className={getStatusColor(session?.status)}>
                        {session?.status?.replace(/_/g, ' ')}
                      </Badge>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-slate-500">Created At</dt>
                    <dd>{session?.created_at && new Date(session.created_at).toLocaleString()}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-slate-500">Submitted At</dt>
                    <dd>{session?.submitted_at ? new Date(session.submitted_at).toLocaleString() : 'Not submitted'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-slate-500">Required Rooms</dt>
                    <dd>{session?.required_rooms?.join(', ') || 'N/A'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-slate-500">Completed Rooms</dt>
                    <dd>{session?.completed_rooms?.join(', ') || 'N/A'}</dd>
                  </div>
                </dl>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Outcome Dialog */}
      <Dialog open={outcomeDialog} onOpenChange={setOutcomeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review Flag</DialogTitle>
            <DialogDescription>
              Record your decision for this flag. Your feedback helps improve AI accuracy.
            </DialogDescription>
          </DialogHeader>
          
          {selectedFlag && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="font-medium">{selectedFlag.reason_code?.replace(/_/g, ' ')}</p>
                <p className="text-sm text-slate-600 mt-1">{selectedFlag.description}</p>
              </div>
              
              <Textarea
                placeholder="Add notes about your decision (optional)..."
                value={outcomeNotes}
                onChange={(e) => setOutcomeNotes(e.target.value)}
              />
            </div>
          )}
          
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setOutcomeDialog(false);
                setOutcomeNotes('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              className="text-amber-600 border-amber-600 hover:bg-amber-50"
              onClick={() => handleOutcome(selectedFlag?.id, 'follow_up')}
              disabled={submittingOutcome}
            >
              <Clock className="h-4 w-4 mr-2" />
              Follow Up
            </Button>
            <Button
              variant="outline"
              className="text-slate-600"
              onClick={() => handleOutcome(selectedFlag?.id, 'insufficient_evidence')}
              disabled={submittingOutcome}
            >
              Insufficient Evidence
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={() => handleOutcome(selectedFlag?.id, 'confirmed')}
              disabled={submittingOutcome}
            >
              <ThumbsUp className="h-4 w-4 mr-2" />
              Confirm Issue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScanResults;
